import * as R from "ramda";

const KNOWN_ERROR_FIELDS = ["message", "code", "details", "hint", "name"] as const;

const CONTEXT_FIELDS = ["status", "statusText"] as const;

const MAX_SERIALIZED_LENGTH = 4096;

const FORMAT_ERROR_FALLBACK = "[formatError failed]";

const isDefinedNonEmpty = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

const safeStringForKey = (value: object, key: string): string | null => {
  try {
    const fieldValue = (value as Record<string, unknown>)[key];
    if (!isDefinedNonEmpty(fieldValue)) {
      return null;
    }
    if (typeof fieldValue !== "object") {
      return String(fieldValue);
    }
    try {
      return JSON.stringify(fieldValue);
    } catch {
      return null;
    }
  } catch {
    return null;
  }
};

const serializeFields = (value: object, fields: readonly string[]): Record<string, string> =>
  R.mergeAll(
    (fields as string[]).map((key) => {
      const stringValue = safeStringForKey(value, key);
      return stringValue === null ? {} : { [key]: stringValue };
    })
  );

const serializeErrorProps = (value: object): Record<string, string> =>
  serializeFields(value, KNOWN_ERROR_FIELDS);

const readContext = (value: object): unknown =>
  R.prop("context" as never, value);

const serializeContextProps = (value: object): Record<string, string> => {
  const context = readContext(value);
  if (context === null || context === undefined || typeof context !== "object") {
    return {};
  }
  return serializeFields(context as object, CONTEXT_FIELDS);
};

const truncate = (value: string): string =>
  value.length > MAX_SERIALIZED_LENGTH ? `${value.slice(0, MAX_SERIALIZED_LENGTH)}...[truncated]` : value;

const jsonStringifyOrNull = (value: object): string | null => {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
};

const safeStringify = (value: object): string =>
  truncate(jsonStringifyOrNull(value) ?? String(value));

export const formatError = (error: unknown): string => {
  try {
    if (error === null || error === undefined) {
      return String(error);
    }

    if (typeof error === "string") {
      return error;
    }

    if (typeof error !== "object") {
      return String(error);
    }

    const structuredFields = { ...serializeErrorProps(error), ...serializeContextProps(error) };
    if (!R.isEmpty(structuredFields)) {
      return safeStringify(structuredFields);
    }

    if (error instanceof Error) {
      return error.message || error.name || "Error";
    }

    return safeStringify(error);
  } catch {
    return FORMAT_ERROR_FALLBACK;
  }
};

export const normalizeError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(formatError(error));

export const logError = (context: string, error: unknown): void => {
  // eslint-disable-next-line no-console
  console.error(`${context}:`, formatError(error), error);
};
